// src/pages/adminDashboard/components/CompanyForm.js
import React, { useEffect, useState, useRef } from "react";
import ReactPhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Form, Input, Button, DatePicker, Select, Typography, Row, Col, Modal, message, Divider } from "antd";
import { LockOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import {
  saveInstitution,
  updateInstitution,
  fetchCountries,
  fetchCurrencies,
  fetchIndustries,
  fetchSubscriptionModels,
  fetchInstitutions,
} from "../AdminDashboardThunk";
import { clearCurrent } from "../AdminDashboardSlice";
import dayjs from "dayjs";
import TimeZoneSelect from "../../../components/TimeZoneSelect/TimeZoneSelect";
import { indianStates } from "../../../lib/currency";
import { verifyPasswordApi } from "../../../apiservice/setting"; // ✅ new API

const { Option } = Select;
const { Text } = Typography;

const CompanyForm = ({ onClose, authInfo }) => {
  const [form] = Form.useForm();
  const [filteredIndustries, setFilteredIndustries] = useState([]);
  const dispatch = useDispatch();
  const { current, saving, countries, subscriptionModels, currencies, industries } =
    useSelector((s) => s.adminDashboard);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [gstEnabled, setGstEnabled] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const tatmeenEnabled = Form.useWatch("is_tatmeen_enabled", form);

  // ── password modal state ─────────────────────────────────────────────────
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [passwordVerifying, setPasswordVerifying] = useState(false);
  // holds the full payload while we wait for password confirmation
  const pendingPayloadRef = useRef(null);

  const selectedModel = subscriptionModels.find((m) => m.id === selectedSubscription);

  const isPharmaSubscription =
    selectedModel?.subscription_type
      ?.toLowerCase()
      .includes("pharma");

  const isUaePharmaSubscription =
    selectedCountry === "United Arab Emirates" &&
    isPharmaSubscription;

  const allowTrial =
    selectedModel &&
    ["ForthERP_Basic", "ForthBooks_Basic", "PharmaERP_Basic", "PharmaBooks_Basic","ForthCRM_Basic"].includes(
      selectedModel.subscription_type
    );

  // ─── fetch lookups ──────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchCountries());
    dispatch(fetchCurrencies());
    dispatch(fetchIndustries());
    dispatch(fetchSubscriptionModels());
  }, [dispatch]);

  // ─── reset GST fields when country changes ──────────────────────────────────
  useEffect(() => {
    if (selectedCountry !== "India") {
      form.setFieldsValue({ state: null, gst_enabled: null });
      setGstEnabled(null);
    }
  }, [selectedCountry, form]);

  // ─── reset state when GST toggled off ──────────────────────────────────────
  useEffect(() => {
    if (gstEnabled !== "yes") {
      form.setFieldsValue({ state: null, road: null });
    }
  }, [gstEnabled, form]);

  // ─── populate form when editing ────────────────────────────────────────────
  useEffect(() => {
    if (current) {
      const cleanTimeZone = (tz) => {
        if (!tz) return tz;
        try {
          let parsed = tz;
          while (typeof parsed === "string" && parsed.startsWith('"')) {
            parsed = JSON.parse(parsed);
          }
          return parsed;
        } catch {
          return tz;
        }
      };

      form.setFieldsValue({
        ...current,
        trial_mode: current.trial_mode || "no",
        time_zone: cleanTimeZone(current.time_zone),
        industry: current.company_type,
        subscription_expiry: current.subscription_expiry
          ? dayjs(current.subscription_expiry)
          : null,
        state: current.province,
        transaction_lock_date: current.transaction_lock_date
          ? dayjs(current.transaction_lock_date)
          : null,
        gln_no_expiry: current.gln_no_expiry
          ? dayjs(current.gln_no_expiry)
          : null,
        tatmeen_username: current.tatmeen_username || null,
        tatmeen_password: current.tatmeen_password || null,
      });

      setSelectedSubscription(current.subscription_type);
      setSelectedCountry(current.country);
      setGstEnabled(current.gst_enabled);
    } else {
      form.resetFields();
    }
  }, [current, form]);

  // ─── filter industries by subscription model ────────────────────────────────
  useEffect(() => {
    if (!selectedModel || !industries.length) return;

    const modelName = selectedModel.subscription_type;

    const forthPlans = [
      "ForthERP_Basic", "ForthERP_Standard", "ForthERP_Pro",
      "ForthBooks_Basic", "ForthBooks_Standard", "ForthBooks_Pro",
      "HRPayroll_Pro", "HRPayroll_Standard", "HRPayroll_Basic","ForthCRM_Basic","ForthCRM_Standard","ForthCRM_Pro",
    ];
    const pharmaPlans = [
      "PharmaERP_Basic", "PharmaERP_Standard", "PharmaERP_Pro",
      "PharmaBooks_Basic", "PharmaBooks_Standard", "PharmaBooks_Pro",
    ];

    let newIndustries = [];
    if (forthPlans.includes(modelName)) {
      newIndustries = industries.filter(
        (item) => !["Healthcare", "Pharmacy", "Drug Store"].includes(item.value || item.name)
      );
    } else if (pharmaPlans.includes(modelName)) {
      newIndustries = industries.filter((item) =>
        ["Healthcare", "Others", "Pharmacy", "Drug Store"].includes(item.value || item.name)
      );
    } else {
      newIndustries = industries;
    }

    setFilteredIndustries(newIndustries);

    const currentIndustry = form.getFieldValue("industry");
    const isValid = newIndustries.some((item) => (item.value || item.name) === currentIndustry);
    if (!isValid) {
      form.setFieldsValue({
        industry: newIndustries[0]?.value || newIndustries[0]?.name || null,
      });
    }
  }, [selectedModel, industries, form]);

  // ─── helpers ─────────────────────────────────────────────────────────────────

  // Check whether transaction_lock_date has changed from what's saved
  const lockDateChanged = (values) => {
    if (!current) return false;

    const newLock = values.transaction_lock_date
      ? values.transaction_lock_date.format("YYYY-MM-DD")
      : null;
    const oldLock = current.transaction_lock_date
      ? dayjs(current.transaction_lock_date).format("YYYY-MM-DD")
      : null;

    return newLock !== oldLock;
  };

  // Build the payload object from form values
  const buildPayload = (values) => ({
    name: values.name,
    company_title: values.company_title,
    company_email: values.company_email,
    admin_email: values.admin_email,
    admin_name: values.admin_name,
    admin_password: values.password,
    street_address: values.street_address,
    phone_no: values.phone_no,
    country_code: values.country_code,
    country: values.country,
    currency: values.currency,
    industry: values.industry,
    subscription_type: values.subscription_type,
    subscription_expiry: values.subscription_expiry?.format("YYYY-MM-DD HH:mm:ss"),
    total_user_count: Number(values.total_user_count),
    total_branch_count: Number(values.total_branch_count),
    basic_user_limit: Number(values.basic_user_limit || 0),
    core_user_limit: Number(values.core_user_limit || 0),
    other: values.other || "",
    time_zone: values.time_zone,
    updated_by: authInfo?.userInfo?.user_id,
    state: values.state || null,
    gst_enabled: values.gst_enabled || "no",
    trial_mode: values.trial_mode || "no",
    transaction_lock_date: values.transaction_lock_date
      ? values.transaction_lock_date.format("YYYY-MM-DD")
      : null,
    tatmeen_client_id: values.tatmeen_client_id || null,

    tatmeen_client_secret:
      values.tatmeen_client_secret || null,

    tatmeen_environment:
      values.tatmeen_environment || null,

    is_tatmeen_enabled:
      values.is_tatmeen_enabled || "no",

    gln_no: values.gln_no || null,
    gln_no_expiry: values.gln_no_expiry
      ? values.gln_no_expiry.format("YYYY-MM-DD")
      : null,
    tatmeen_username: values.tatmeen_username || null,
    tatmeen_password: values.tatmeen_password || null,
  });

  // Actually dispatch the update/create after all checks pass
  const dispatchSave = (payload) => {
    if (current) {
      dispatch(updateInstitution({ ...payload, id: current.id }))
        .unwrap()
        .then(() => {
          dispatch(fetchInstitutions());
          onClose();
        });
    } else {
      dispatch(saveInstitution(payload))
        .unwrap()
        .then(() => {
          dispatch(fetchInstitutions());
          onClose();
        })
        .catch((err) => {
          Swal.fire({
            icon: "error",
            title: "Error",
            text:
              !err || err === "Request failed"
                ? "Company already exists or something is wrong."
                : err,
            confirmButtonColor: "#3085d6",
          });
        });
    }
  };

  // ─── submit ─────────────────────────────────────────────────────────────────
  const onFinish = (values) => {
    const payload = buildPayload(values);
    delete payload.password;
    delete payload.confirm_password;

    // If transaction_lock_date changed, gate behind password modal
    if (lockDateChanged(values)) {
      pendingPayloadRef.current = payload;
      setAdminPassword("");
      setPasswordModalVisible(true);
      return; // ← hold, wait for password confirmation
    }

    // No lock date change — proceed normally
    dispatchSave(payload);
  };

  // ─── password modal confirm ──────────────────────────────────────────────────
  const handlePasswordConfirm = async () => {
    if (!adminPassword.trim()) {
      message.warning("Please enter your password.");
      return;
    }

    setPasswordVerifying(true);
    try {
      const res = await verifyPasswordApi({ password: adminPassword });

      if (res?.data?.error === false) {
        // Password correct — proceed with the saved payload
        setPasswordModalVisible(false);
        setAdminPassword("");
        dispatchSave(pendingPayloadRef.current);
        pendingPayloadRef.current = null;
      } else {
        message.error(res?.data?.message || "Incorrect password. Please try again.");
      }
    } catch (err) {
      message.error("Error verifying password. Please try again.");
      console.error(err);
    } finally {
      setPasswordVerifying(false);
    }
  };

  const handlePasswordModalCancel = () => {
    setPasswordModalVisible(false);
    setAdminPassword("");
    pendingPayloadRef.current = null;
  };

  // ─── render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={(changedValues, allValues) => {
          if (changedValues.country) setSelectedCountry(changedValues.country);
          if (changedValues.gst_enabled) setGstEnabled(changedValues.gst_enabled);
          if (changedValues.subscription_type) setSelectedSubscription(changedValues.subscription_type);
          if (changedValues.is_tatmeen_enabled) {
            form.setFieldsValue({
              tatmeen_environment:
                changedValues.is_tatmeen_enabled === "no"
                  ? null
                  : allValues.tatmeen_environment,

              tatmeen_client_id:
                changedValues.is_tatmeen_enabled === "no"
                  ? null
                  : allValues.tatmeen_client_id,

              tatmeen_client_secret:
                changedValues.is_tatmeen_enabled === "no"
                  ? null
                  : allValues.tatmeen_client_secret,
            });
          }
        }}
      >
        <Row gutter={16}>
          {/* ── Company Info ── */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="name" label="Company Name" rules={[{ required: true }]}>
              <Input data-testid="company-name-input" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="company_title" label="Company Legal Name" rules={[{ required: true }]}>
              <Input data-testid="company-legal-name-input" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="company_email" label="Email" rules={[{ required: true, type: "email" }]}>
              <Input data-testid="company-email-input" />
            </Form.Item>
          </Col>

          {/* ── Phone ── */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Phone Number" required>
              <Form.Item name="country_code" noStyle rules={[{ required: true, message: "Country code is required" }]}>
                <Input type="hidden" />
              </Form.Item>
              <Form.Item name="phone_no" noStyle rules={[{ required: true, message: "Phone number is required" }]}>
                <Input type="hidden" />
              </Form.Item>
              <ReactPhoneInput
                inputProps={{ "data-testid": "phone-input" }}
                country="us"
                value={
                  form.getFieldValue("country_code")
                    ? `+${form.getFieldValue("country_code")} ${form.getFieldValue("phone_no") || ""}`
                    : ""
                }
                inputStyle={{ width: "100%" }}
                onChange={(value, countryData) => {
                  const countryCode = countryData.dialCode;
                  const phoneNumber = value.replace(`+${countryCode}`, "").trim();
                  const phoneNumberWithoutCountryCode = phoneNumber.startsWith(countryCode)
                    ? phoneNumber.slice(countryCode.length)
                    : phoneNumber;
                  form.setFieldsValue({ country_code: countryCode, phone_no: phoneNumberWithoutCountryCode });
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item name="street_address" label="Street Address" rules={[{ required: true }]}>
              <Input data-testid="street-address-input" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="country" label="Country" rules={[{ required: true }]}>
              <Select data-testid="country-select" placeholder="Select Country" showSearch optionFilterProp="children">
                {countries.map((c) => (
                  <Option key={c.code} value={c.value}>{c.value}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* ── India-only GST fields ── */}
          {selectedCountry === "India" && (
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="gst_enabled" label="GST Module Required?" rules={[{ required: true }]}>
                <Select placeholder="Select Option">
                  <Option value="yes">Yes</Option>
                  <Option value="no">No</Option>
                </Select>
              </Form.Item>
            </Col>
          )}
          {selectedCountry === "India" && gstEnabled === "yes" && (
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="state" label="State" rules={[{ required: true, message: "State is required" }]}>
                <Select placeholder="Select State" showSearch>
                  {indianStates.map((state) => (
                    <Option key={state.code} value={state.name}>{state.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}

          {/* ── Subscription ── */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="currency" label="Currency" rules={[{ required: true }]}>
              <Select data-testid="currency-select" showSearch optionFilterProp="children">
                {currencies.map((cur) => (
                  <Option key={cur.code} value={cur.code}>{cur.code} - {cur.currency}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="subscription_type"
              label="Subscription Model"
              rules={[{ required: true, message: "Subscription Model is required" }]}
            >
              <Select
                placeholder="Select Subscription Model"
                data-testid="subscription-model-select"
                showSearch
                optionFilterProp="children"
                options={subscriptionModels.map((model) => ({
                  value: model.id,
                  label: model.subscription_type,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="subscription_expiry" label="Subscription Expiry" rules={[{ required: true }]}>
              <DatePicker data-testid="subscription-expiry-datepicker" style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>

          {allowTrial && (
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="trial_mode"
                label="Free Mode"
                initialValue="no"
                rules={[{ required: true, message: "Please select Trial Mode" }]}
              >
                <Select placeholder="Select Option">
                  <Option value="yes">Yes</Option>
                  <Option value="no">No</Option>
                </Select>
              </Form.Item>
            </Col>
          )}

          {/* ── Tatmeen Configuration ── */}
          {isUaePharmaSubscription && (
            <>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="is_tatmeen_enabled"
                  label="Enable Tatmeen"
                  initialValue="no"
                  rules={[
                    {
                      required: true,
                      message: "Please select Tatmeen option",
                    },
                  ]}
                >
                  <Select placeholder="Select Option">
                    <Option value="yes">Yes</Option>
                    <Option value="no">No</Option>
                    <Option value="custom">Custom</Option>
                  </Select>
                </Form.Item>
              </Col>

              {(tatmeenEnabled === "yes" || tatmeenEnabled === "custom") && (
                <>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="gln_no"
                      label="GLN Number"
                      rules={[
                        {
                          required: true,
                          message: "GLN Number is required",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="gln_no_expiry"
                      label="GLN Expiry"
                      rules={[
                        {
                          required: true,
                          message: "GLN expiry is required",
                        },
                      ]}
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="tatmeen_environment"
                      label="Tatmeen Environment"
                      rules={[
                        {
                          required: true,
                          message:
                            "Tatmeen environment is required",
                        },
                      ]}
                    >
                      <Select placeholder="Select Environment">
                        <Option value="staging">
                          Staging
                        </Option>
                        <Option value="production">
                          Production
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="tatmeen_client_id"
                      label="Tatmeen Client ID"
                      rules={[
                        {
                          required: true,
                          message:
                            "Tatmeen Client ID is required",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="tatmeen_client_secret"
                      label="Tatmeen Client Secret"
                      rules={[
                        {
                          required: true,
                          message:
                            "Tatmeen Client Secret is required",
                        },
                      ]}
                    >
                      <Input.Password />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="tatmeen_username"
                      label="Tatmeen Username"
                      rules={[
                        {
                          required: true,
                          message: "Tatmeen Username is required",
                        },
                      ]}
                    >
                      <Input placeholder="Enter Tatmeen Username" />
                    </Form.Item>
                  </Col>

                  {/* ✅ NEW: Tatmeen Password Field */}
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="tatmeen_password"
                      label="Tatmeen Password"
                      rules={[
                        {
                          required: true,
                          message: "Tatmeen Password is required",
                        },
                      ]}
                    >
                      <Input.Password placeholder="Enter Tatmeen Password" />
                    </Form.Item>
                  </Col>

                </>
              )}
            </>
          )}

          <Col xs={24} sm={12} md={8}>
            <Form.Item name="industry" label="Industry" rules={[{ required: true }]}>
              <Select data-testid="industry-select" showSearch optionFilterProp="children">
                {filteredIndustries.map((ind) => (
                  <Option key={ind.id || ind.value} value={ind.name || ind.value}>
                    {ind.name || ind.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* ── User / Branch Limits ── */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="total_branch_count" label="Branch Limit" rules={[{ required: true }]}>
              <Input type="number" data-testid="branch-limit-input" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="total_user_count" label="Default User Limit" rules={[{ required: true }]}>
              <Input type="number" data-testid="user-limit-input" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="basic_user_limit" label="Base User Limit">
              <Input type="number" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="core_user_limit" label="Core User Limit">
              <Input type="number" />
            </Form.Item>
          </Col>

          {/* ── Admin Info (create only) ── */}
          {!current && (
            <>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="admin_name" label="Admin Name" rules={[{ required: true }]}>
                  <Input data-testid="admin-name-input" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="admin_email" label="Admin Email" rules={[{ required: true, type: "email" }]}>
                  <Input data-testid="admin-email-input" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="password"
                  label="Admin Password"
                  rules={[
                    { required: true },
                    {
                      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/,
                      message: "Password must include upper, lower, number, special character",
                    },
                  ]}
                  hasFeedback
                >
                  <Input.Password data-testid="admin-password-input" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="confirm_password"
                  label="Confirm Password"
                  dependencies={["password"]}
                  hasFeedback
                  rules={[
                    { required: true },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) return Promise.resolve();
                        return Promise.reject(new Error("Passwords do not match"));
                      },
                    }),
                  ]}
                >
                  <Input.Password data-testid="confirm-password-input" />
                </Form.Item>
              </Col>
            </>
          )}

          {/* ── Time Zone ── */}

          {/* ── Time Zone ── */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item name="time_zone" label="Time Zone" rules={[{ required: true, message: "Time Zone is required" }]}>
              <TimeZoneSelect data-testid="time-zone-select" />
            </Form.Item>
          </Col>

          {/* ── Transaction Lock Date (edit only) ── */}
          {current && (
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="transaction_lock_date"
                label="Transaction Lock Date"
                tooltip="Transactions on or before this date will be locked from edits and deletes. Changing this requires your password."
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="No lock"
                  allowClear
                />
              </Form.Item>
            </Col>
          )}

          {!current && <Col xs={24} sm={12} md={8} />}
        </Row>

        {/* ── Actions ── */}
        <Form.Item style={{ marginTop: "1rem" }}>
          <Button type="primary" htmlType="submit" loading={saving} data-testid="save-company-button">
            {current ? "Update" : "Save"}
          </Button>
          <Button
            style={{ marginLeft: 8 }}
            onClick={() => {
              dispatch(clearCurrent());
              onClose();
            }}
          >
            Cancel
          </Button>
        </Form.Item>
      </Form>

      {/* ── Password Confirmation Modal ── */}
      <Modal
        title={
          <span>
            <LockOutlined style={{ marginRight: 8, color: "#faad14" }} />
            Confirm Identity to Change Lock Date
          </span>
        }
        open={passwordModalVisible}
        onCancel={handlePasswordModalCancel}
        centered
        width={420}
        footer={[
          <Button key="cancel" onClick={handlePasswordModalCancel}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={passwordVerifying}
            onClick={handlePasswordConfirm}
          >
            Confirm & Save
          </Button>,
        ]}
      >
        <p style={{ marginBottom: 16, color: "#555" }}>
          You are changing the transaction lock date. Please enter your admin
          password to confirm this change.
        </p>
        <Input.Password
          prefix={<LockOutlined />}
          placeholder="Enter your password"
          value={adminPassword}
          onChange={(e) => setAdminPassword(e.target.value)}
          onPressEnter={handlePasswordConfirm}
          autoFocus

          autoComplete="new-password"
        />
      </Modal>
    </>
  );
};

export default CompanyForm;